import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { useDateFnsLocale } from '@plato/ui-legacy/lib/hooks/use-date-fns-locale';
import { Calendar } from '@plato/ui/components/calendar';
import {
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverHeader,
    PopoverIconClose,
    PopoverTitle,
    PopoverTrigger,
} from '@plato/ui/containers/popover';
import { useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ViewMode } from '../../events/components/events-view-mode-selector.tsx';

const weekend = { dayOfWeek: [0, 6] };

export type CalendarDatePickerProps = {
    day: Date;
    viewMode: ViewMode;
    disableWeekends: boolean;
    onPick: (day: Date) => void;
    className?: string;
    children: ReactNode;
};

/** A button that opens a calendar: an anchored card on desktop, a bottom sheet on phones. */
export function CalendarDatePicker({
    day,
    viewMode,
    disableWeekends,
    onPick,
    className,
    children,
}: CalendarDatePickerProps): ReactElement {
    const { t } = useTranslation();
    const locale = useDateFnsLocale(useResolvedLocale());
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const pick = (picked: Date): void => {
        onPick(picked);
        setIsOpen(false);
    };
    const title =
        viewMode === 'week'
            ? t('date_picker__pick_week', { ns: 'events' })
            : t('date_picker__pick_day', { ns: 'events' });

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger render={<button type="button" className={className} />}>
                {children}
            </PopoverTrigger>
            <PopoverContent className="sm:w-fit" aria-label={title}>
                {/* The phone sheet needs a title and a way out; PopoverHeader renders nothing on desktop */}
                <PopoverHeader>
                    <PopoverTitle>{title}</PopoverTitle>
                    <PopoverIconClose className="ml-auto" />
                </PopoverHeader>
                <PopoverBody className="flex justify-center px-default py-1 sm:p-default">
                    {/* Two elements: `mode` picks the type of `onSelect`, so it can't be computed */}
                    {viewMode === 'week' ? (
                        <Calendar
                            mode="week"
                            locale={locale}
                            defaultMonth={day}
                            selected={day}
                            disabled={disableWeekends ? weekend : undefined}
                            onSelect={pick}
                        />
                    ) : (
                        <Calendar
                            mode="single"
                            locale={locale}
                            defaultMonth={day}
                            selected={day}
                            disabled={disableWeekends ? weekend : undefined}
                            onSelect={(picked) => {
                                // DayPicker unselects on a second click; read it as picking the same day
                                pick(picked ?? day);
                            }}
                        />
                    )}
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}
